import {
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import { useState } from "react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import useCores from "../../../core/hooks/useCores";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useContas } from "../../hooks/useContas";
import { IComboMaisVendido } from "../../interfaces/IComboMaisVendido";

export const RelatorioCombosMaisVendidos = () => {
    const { real } = useMoeda();
    const { buscarCombosMaisVendidos, loading } = useContas();

    const [dataInicio, setDataInicio] = useState<Moment | null>(null);
    const [dataFim, setDataFim] = useState<Moment | null>(null);
    const [dados, setDados] = useState<IComboMaisVendido[]>([]);

    const cores = useCores('vibrant', Math.min(dados.length, 5));

    const totalCombos = dados.reduce((soma, combo) => soma + combo.quantidade, 0);

    const handleFiltrar = async () => {
        const resultado = await buscarCombosMaisVendidos(
            dataInicio ? dataInicio.format('YYYY-MM-DD') : undefined,
            dataFim ? dataFim.format('YYYY-MM-DD') : undefined
        );
        if (resultado) setDados(resultado);
    };

    return (
        <div style={{ maxWidth: '95%', margin: '0 auto', paddingTop: '1rem' }}>
            <Paper sx={{ padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <DatePicker
                        label="Data início"
                        value={dataInicio}
                        onChange={setDataInicio}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: 'small' } }}
                    />
                    <DatePicker
                        label="Data fim"
                        value={dataFim}
                        onChange={setDataFim}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: 'small' } }}
                    />
                    <Button variant="contained" size="small" onClick={handleFiltrar} disabled={loading}>
                        {loading ? <CircularProgress size={18} /> : 'Filtrar'}
                    </Button>
                </div>
            </Paper>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <CircularProgress />
                </div>
            ) : dados.length === 0 ? (
                <Paper sx={{ padding: '2rem' }}>
                    <Typography align="center">
                        Nenhum combo vendido no período. Aplique os filtros e clique em Filtrar.
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Paper sx={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" fontWeight="bold">Total de combos vendidos</Typography>
                            <Typography variant="h5" fontWeight={800} color="primary.main">{totalCombos}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ padding: '0.5rem', height: '500px' }}>
                            <Typography sx={{ padding: '1rem', position: 'absolute' }} variant="subtitle1" fontWeight="bold">
                                Top 5 combos mais vendidos
                            </Typography>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={dados.slice(0, 5)}
                                        dataKey="quantidade"
                                        nameKey="descricao"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label={({ name }) => name && name.length > 10 ? `${name.substring(0, 10)}...` : name}
                                    >
                                        {dados.slice(0, 5).map((entry, index) => (
                                            <Cell key={`cell-${entry.comboReferenciaUuid}-${index}`} fill={cores[index]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value: number) => `${value} combos`} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper>
                            <TableContainer>
                                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                                    <Typography variant="h6" fontWeight="800">
                                        Combos mais vendidos
                                    </Typography>
                                </Toolbar>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Combo</TableCell>
                                            <TableCell>Quantidade</TableCell>
                                            <TableCell>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.map((combo, index) => (
                                            <TableRow key={`combo-${combo.comboReferenciaUuid}-${index}`}>
                                                <TableCell>{combo.descricao}</TableCell>
                                                <TableCell>{combo.quantidade}</TableCell>
                                                <TableCell>{real(combo.valorTotal)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </div>
    );
};
